import { useState, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import debounce from "lodash/debounce";
import PredictionsList from "./location/PredictionsList";
import { useGoogleMapsServices, getPlaceDetails } from "@/hooks/useGoogleMapsServices";

interface LocationSearchInputProps {
  onLocationSelect: (location: {
    lat: number;
    lng: number;
    formattedAddress: string;
    placeId?: string;
    businessDetails?: {
      name?: string;
      phoneNumber?: string;
      website?: string;
      rating?: number;
      userRatingsTotal?: number;
      reviews?: google.maps.places.PlaceReview[];
      photoUrl?: string;
    };
  }) => void;
  placeholder?: string;
  defaultValue?: string;
}

const LocationSearchInput = ({
  onLocationSelect,
  placeholder = "Enter your business address or name",
  defaultValue = "",
}: LocationSearchInputProps) => {
  const [address, setAddress] = useState(defaultValue);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const { toast } = useToast();
  const { autocomplete, placesService } = useGoogleMapsServices();

  const searchAddress = async (input: string) => {
    if (!input.trim() || !autocomplete) return;

    try {
      const establishmentResponse = await new Promise<google.maps.places.AutocompletePrediction[]>(
        (resolve, reject) => {
          autocomplete.getPlacePredictions(
            {
              input,
              types: ['establishment'],
              componentRestrictions: { country: 'us' }
            },
            (results, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                resolve(results);
              } else {
                reject(status);
              }
            }
          );
        }
      ).catch(() => []);

      const addressResponse = await new Promise<google.maps.places.AutocompletePrediction[]>(
        (resolve, reject) => {
          autocomplete.getPlacePredictions(
            {
              input,
              types: ['address'],
              componentRestrictions: { country: 'us' }
            },
            (results, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                resolve(results);
              } else {
                reject(status);
              }
            }
          );
        }
      ).catch(() => []);

      const combinedResults = [...establishmentResponse, ...addressResponse];
      const uniqueResults = Array.from(new Map(combinedResults.map(item => [item.place_id, item])).values());
      
      setPredictions(uniqueResults);
      setShowPredictions(uniqueResults.length > 0);
    } catch (error) {
      console.error('Error getting predictions:', error);
      setPredictions([]);
    }
  };

  const handlePredictionSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesService) return;

    try {
      const place = await getPlaceDetails(placesService, prediction.place_id);

      if (place.geometry?.location && place.formatted_address) {
        let photoUrl: string | undefined;
        if (place.photos && place.photos.length > 0) {
          photoUrl = place.photos[0].getUrl();
        }

        const locationData = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          formattedAddress: place.formatted_address,
          placeId: place.place_id,
          businessDetails: {
            name: place.name,
            phoneNumber: place.formatted_phone_number,
            website: place.website,
            rating: place.rating,
            userRatingsTotal: place.user_ratings_total,
            reviews: place.reviews,
            photoUrl
          }
        };

        onLocationSelect(locationData);
        setAddress(place.formatted_address);
        setShowPredictions(false);
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      toast({
        title: "Error",
        description: "Failed to get location details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => searchAddress(value), 300),
    [autocomplete]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress(value);
    debouncedSearch(value);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowPredictions(false);
    }, 200);
  };

  return (
    <div className="relative w-full">
      <Input
        type="text"
        value={address}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="w-full"
      />
      <PredictionsList
        predictions={predictions}
        onSelect={handlePredictionSelect}
        show={showPredictions}
      />
    </div>
  );
};

export default LocationSearchInput;